<template>
  <div class="v-select-wrapper w-full">
    <v-autocomplete
      ref="autocomplete"
      :class="{ 'v-autocomplete--with-label': !!label }"
      :value="value"
      :items="items"
      :search-input.sync="search"
      :loading="loading"
      :placeholder="placeholder"
      :disabled="disabled"
      :rules="allRules"
      :hide-details="hideDetails"
      :error-messages="allErrorMessages"
      :validate-on-blur="validateOnBlur"
      :menu-props="{ offsetY: true }"
      :hide-no-data="loading"
      :filter="filterItem"
      item-text="text"
      item-value="value"
      persistent-placeholder
      append-icon="mdi-chevron-down"
      @input="onInput"
      @change="$emit('change', $event)"
      @blur="$emit('blur', $event)">
      <template #label>
        {{ label }}
        <span v-if="required" class="required__icon">*</span>
      </template>
      <template #no-data>
        <div class="v-list-item theme--light">
          <div class="v-list-item__content">
            <div class="v-list-item__title">{{ $t("generic.no_results_found") }}</div>
          </div>
        </div>
      </template>
      <template #append-item>
        <div v-if="hasMore" v-intersect="onLoadMoreIntersect" class="domain-autocomplete__load-more">
          <v-progress-circular v-if="loading" indeterminate size="18" width="2" color="primary" />
        </div>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
  import debounce from "lodash.debounce";
  import { DOMAIN_STATUS } from "@/constant";
  import { formErrorMessage } from "@/utility/formErrorMessage";

  const PER_PAGE = 25;
  const SEARCH_DEBOUNCE_MS = 350;

  export default {
    name: "VaDomainAutocomplete",
    inject: {
      formState: { default: undefined },
    },
    props: {
      value: { type: String, default: null },
      source: { type: String, default: null },
      initialDomain: { type: Object, default: null },
      label: { type: String, default: "" },
      placeholder: { type: String, default: "" },
      disabled: { type: Boolean, default: false },
      required: { type: Boolean, default: false },
      rules: { type: Array, default: () => [] },
      hideDetails: { type: [Boolean, String], default: "auto" },
      errorMessages: { type: Array, default: () => [] },
      validateOnBlur: { type: Boolean, default: false },
    },
    data() {
      return {
        items: [],
        selectedItem: null,
        search: null,
        loading: false,
        fetchToken: 0,
        page: 1,
        hasMore: true,
        listQuery: null,
      };
    },
    computed: {
      allRules() {
        const rules = [...this.rules];
        if (this.required) {
          rules.push(v => !!v || this.$t("va.forms.required_field", { field: this.label }));
        }

        return rules;
      },
      allErrorMessages() {
        const formErrors = (this.source && this.formState?.errors?.[this.source]) || [];

        return [...this.errorMessages, ...formErrors];
      },
      query() {
        const search = (this.search || "").trim();

        return search && search !== this.selectedItem?.text ? search : "";
      },
    },
    watch: {
      value: {
        immediate: true,
        handler(iri) {
          this.ensureSelected(iri);
        },
      },
      query() {
        this.debouncedQueryChange();
      },
    },
    created() {
      this.debouncedQueryChange = debounce(() => {
        if (this.isMenuActive()) this.ensureFresh();
      }, SEARCH_DEBOUNCE_MS);
    },
    mounted() {
      this.$watch(
        () => this.$refs.autocomplete.isMenuActive,
        active => {
          if (active) this.ensureFresh();
        },
      );
    },
    beforeDestroy() {
      this.debouncedQueryChange.cancel();
      this.fetchToken += 1;
    },
    methods: {
      toItem(domain) {
        return {
          text: domain.url,
          value: domain["@id"],
          id: domain.id,
          name: domain.name,
          sentryEnabled: domain.sentryEnabled,
        };
      },
      isMenuActive() {
        return !!this.$refs.autocomplete?.isMenuActive;
      },
      filterItem(item) {
        if (item.value !== this.selectedItem?.value || !this.query) return true;
        const query = this.query.toLowerCase();

        return [item.text, item.name].some(text => (text || "").toLowerCase().includes(query));
      },
      onInput(iri) {
        this.selectedItem = this.items.find(item => item.value === iri) || null;
        this.$emit("input", iri);
        this.$emit("item-selected", this.selectedItem);
      },
      async ensureSelected(iri) {
        if (!iri) {
          this.selectedItem = null;
          this.$emit("item-selected", null);

          return;
        }
        if (this.selectedItem?.value === iri) return;

        const known = this.items.find(item => item.value === iri);
        if (known) {
          this.select(known);

          return;
        }

        if (this.initialDomain?.["@id"] === iri && this.initialDomain.url) {
          this.select(this.toItem(this.initialDomain));

          return;
        }

        try {
          const { data } = await this.$admin.dataProvider.getOne("domains", { id: iri.replace("/api/domains/", "") });
          if (this.value !== iri) return;
          this.select(this.toItem(data));
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        }
      },
      select(item) {
        this.selectedItem = item;
        this.mergeItems([item]);
        this.$emit("item-selected", item);
      },
      mergeItems(fetched) {
        const seen = new Set(this.items.map(item => item.value));
        this.items = [...this.items, ...fetched.filter(item => !seen.has(item.value))];
      },
      ensureFresh() {
        if (this.listQuery !== this.query) this.resetAndLoad();
      },
      resetAndLoad() {
        this.page = 1;
        this.hasMore = true;
        this.listQuery = this.query;
        this.items = this.selectedItem ? [this.selectedItem] : [];
        this.loadMore();
      },
      async loadMore() {
        if (!this.hasMore) return;
        const token = ++this.fetchToken;
        const isStale = () => token !== this.fetchToken;
        const query = this.listQuery;
        const page = this.page;
        this.loading = true;
        try {
          const { data = [], total } = await this.$admin.dataProvider.getList("domains/choices", {
            filter: { status: DOMAIN_STATUS.CONNECTED, ...(query && { q: query }) },
            pagination: { page, perPage: PER_PAGE },
          });
          if (isStale()) return;
          this.mergeItems(data.map(this.toItem));
          this.hasMore = typeof total === "number" ? page * PER_PAGE < total : data.length >= PER_PAGE;
          this.page = page + 1;
        } catch (e) {
          if (isStale()) return;
          if (page === 1) this.listQuery = null;
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          if (!isStale()) this.loading = false;
        }
      },
      onLoadMoreIntersect(_entries, _observer, isIntersecting) {
        if (!isIntersecting || this.loading || !this.hasMore) return;
        this.loadMore();
      },
    },
  };
</script>

<style lang="scss" scoped>
  .domain-autocomplete__load-more {
    display: flex;
    justify-content: center;
    min-height: 32px;
    padding: 8px 0;
  }
</style>
